import omitBy from "lodash/omitBy";
import pick from "lodash/pick";
import sortBy from "lodash/sortBy";

import {GlobalPingProbeLocation} from "@/components/globalPingProbeLocationSelect/GlobalPingProbeLocationSelect.types.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {CheckFormResult} from "@/lib/queries/checks.ts";
import {
  IntervalFrequency,
  MSPInterval,
  MonitoringServiceChoices,
  MonitoringServiceType,
  ScheduleState,
  ServiceDefinition,
  ServiceInput,
  ThirdPartyMonitoringType,
} from "@/lib/types/checks.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";
import {
  DefaultLocationsNumber,
  MonitoringGroup,
  MonitoringGroupLevel,
  MonitoringGroupType,
  MonitoringGroupsArchMapperEntry,
  MonitoringServer,
} from "@/lib/types/monitoring.ts";

interface ResolverProps {
  service?: ServiceInput;
  svc: ServiceDefinition;
  mspInterval: MSPInterval;
  serviceType: MonitoringServiceType;
  defaultGroups: MonitoringGroup[];
  checkFrequency: CheckFrequency;
  contactGroups: ContactGroup[];
  mspChoices: MonitoringServiceChoices;
  groupCheckServices?: number[];
}

export const generateFormDefaults = ({
  svc,
  service,
  mspInterval,
  serviceType,
  defaultGroups,
  checkFrequency,
  contactGroups,
  mspChoices,
  groupCheckServices,
}: ResolverProps) => {
  const interval = svc.fields.find((f) => f.field === ServiceField.INTERVAL);
  let intervalDefault = mspInterval.default;
  // override default with account/plan frequency
  if (interval?.mapped_fields?.frequency) {
    const planFrequencies = interval?.mapped_fields?.frequency;
    const planInterval = (planFrequencies as IntervalFrequency)[checkFrequency] ?? planFrequencies;
    if (planInterval > intervalDefault) {
      // don't suggest 1 minute if default is 5 minutes
      intervalDefault = planInterval;
    }
  }

  const optionals = svc.fields
    .filter((f) => f.default !== null)
    // eslint-disable-next-line typescript/no-explicit-any
    .reduce((acc: Record<string, any>, f) => {
      const selectField = mspChoices[f.field];
      acc[f.field] = selectField ? f.default.toString() : f.default;
      return acc;
    }, {});
  let escalations =
    service?.escalations?.map((e) => ({
      wait_time: e.wait_time,
      num_repeats: e.num_repeats,
      contact_groups: e.contact_groups.map((g: ContactGroup) => g.id),
    })) || [];
  if (escalations.length < 1) {
    escalations = [{num_repeats: 0, contact_groups: [], wait_time: 0}];
  }

  const defaultContacts = sortBy(contactGroups, "id").slice(0, 1);
  const flaggedFields = new Set(svc.fields.filter((f) => f.feature_flag).map((f) => f.field));
  return {
    ...optionals,
    ...omitBy(pick(service, Object.values(ServiceField)), (value, key) => value === null && flaggedFields.has(key)),
    [ServiceField.INTERVAL]: service?.msp_interval ? service.msp_interval : intervalDefault,
    tags: service?.tags?.map((g) => g.id) || [],
    monitoring_service_type: service?.monitoring_service_type || serviceType,
    msp_version: service?.msp_version || svc.versions?.at(-1)!.version,
    contact_groups: (service?.contact_groups || defaultContacts).map((g) => g.id),
    schedule_state: service?.schedule_state || ScheduleState.ACTIVE,
    monitoring_groups: service?.monitoring_groups?.map((g) => g.id) || defaultGroups.map((l) => l.id),
    escalations: escalations,
    selected_third_party_service_component: service?.selected_third_party_service_component,
    notify_only_on_down: service?.notify_only_on_down || false,
    group_check_services: service?.group_check_services?.map((g) => g.id) || groupCheckServices || [],
    group_check_tags: service?.group_check_tag_ids || [],
    pause_on_scheduled_maintenance: service?.pause_on_scheduled_maintenance || false,
    third_party_monitoring_type: service?.third_party_monitoring_type || ThirdPartyMonitoringType.ALL,
    third_party_group: service?.third_party_group || null,
    third_party_components: service?.third_party_components || [],
    is_under_manual_maintenance: service?.is_under_manual_maintenance || false,
    pause_under_manual_maintenance: service?.pause_under_manual_maintenance || false,
    maintenance_schedule_ids: service?.maintenance_schedule_ids || [],
    network_locations:
      ((service as unknown as Record<string, unknown>)?.network_locations as GlobalPingProbeLocation[]) ?? [],
    network_name: ((service as unknown as Record<string, unknown>)?.network_name as string) ?? "",
    network_capture_traceroute: Boolean(
      (service as unknown as Record<string, unknown>)?.network_capture_traceroute ?? false
    ),
    network_no_probe_fallback:
      ((service as unknown as Record<string, unknown>)?.network_no_probe_fallback as "UP" | "DOWN") || "UP",
  };
};

export const getMonitoringGroupsLimit = ({
  limit_monitoring_groups,
  limit_monitoring_group_level,
  defaultLocationsNumber,
  groupType,
}: {
  groupType: MonitoringGroupType;
  limit_monitoring_groups: number;
  limit_monitoring_group_level: MonitoringGroupLevel;
  defaultLocationsNumber: DefaultLocationsNumber;
}): number => {
  const limitedByAccount =
    defaultLocationsNumber.byLevel.find((g) => g.level === limit_monitoring_group_level)?.number || 0;
  const limitedByType = defaultLocationsNumber.byType.find((g) => g.type === groupType)?.number || 0;
  return Math.min(limit_monitoring_groups, limitedByAccount, limitedByType);
};

export const preserveLocations = ({
  IDsToPreserve,
  monitoringGroups,
  limit,
  tierLimit,
}: {
  IDsToPreserve: number[];
  monitoringGroups: MonitoringGroup[];
  limit: number;
  tierLimit: MonitoringGroupLevel;
}): MonitoringGroup[] => {
  const locationsToPreserve = monitoringGroups.filter((g) => IDsToPreserve.includes(g.id));
  if (locationsToPreserve.length === 0) {
    const availableLocations = monitoringGroups.filter((g) => !IDsToPreserve.includes(g.id));
    return locationsToPreserve
      .concat(availableLocations)
      .filter((mg) => mg.level <= tierLimit)
      .slice(0, limit);
  }

  return locationsToPreserve;
};

export const filterAllowedMonitoringGroupsForCheckType = ({
  monitoringGroupsArchMapper,
  serviceDef,
  monitoringServiceType,
  mspVersion,
  monitoringGroups,
}: {
  monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
  serviceDef: ServiceDefinition;
  monitoringServiceType: MonitoringServiceType;
  mspVersion?: number;
  monitoringGroups: MonitoringGroup[];
}): MonitoringGroup[] => {
  // TODO: BELGRADE: This filtering function & all uses should be removed once NAGIOS has been fully decommissioned.
  const version = mspVersion || serviceDef.versions.at(-1)!.version;
  const allowedMonitoringGroups = monitoringGroupsArchMapper.find(
    (x) => x.monitoring_service_type === monitoringServiceType && x.version === version
  );
  if (allowedMonitoringGroups) {
    monitoringGroups = monitoringGroups.filter((mg) => allowedMonitoringGroups.monitoring_group_ids.includes(mg.id));
  }
  return monitoringGroups;
};

export const getDefaultMonitoringGroups = ({
  formData,
  serviceDef,
  preserveIds = [],
  requestedCount,
  locationsCapFallback = 0,
}: {
  formData: CheckFormResult;
  serviceDef: ServiceDefinition;
  preserveIds?: number[];
  requestedCount?: number;
  locationsCapFallback?: number;
}): MonitoringGroup[] => {
  const groupType = serviceDef.force_monitoring_group_type || MonitoringGroupType.NORMAL;
  const locationsLimit = getMonitoringGroupsLimit({
    limit_monitoring_groups: formData.user.account.limit_monitoring_groups || locationsCapFallback,
    limit_monitoring_group_level: formData.user.account.limit_monitoring_group_level,
    groupType,
    defaultLocationsNumber: formData.defaultLocationsNumber,
  });
  const locationsPerType = filterAllowedMonitoringGroupsForCheckType({
    monitoringGroupsArchMapper: formData.monitoringGroupsArchMapper,
    serviceDef,
    monitoringServiceType: serviceDef.service_type,
    mspVersion: undefined,
    monitoringGroups: formData.monitoringGroups.filter((group) => group.type === groupType),
  });
  const preservedLocations = preserveLocations({
    IDsToPreserve: preserveIds,
    monitoringGroups: locationsPerType,
    limit: requestedCount ? Math.min(requestedCount, locationsLimit) : locationsLimit,
    tierLimit: formData.user.account.limit_monitoring_group_level,
  });
  return serviceDef.force_monitoring_group_limit === 1 && preservedLocations.length
    ? [preservedLocations[0]]
    : preservedLocations;
};

export const filterAllowedMonitoringServersForCheckType = ({
  monitoringGroupsArchMapper,
  serviceDef,
  monitoringServiceType,
  mspVersion,
  monitoringServers,
}: {
  monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
  serviceDef: ServiceDefinition;
  monitoringServiceType: MonitoringServiceType;
  mspVersion?: number;
  monitoringServers: MonitoringServer[];
}): MonitoringServer[] => {
  // TODO: BELGRADE: This filtering function & all uses should be removed once NAGIOS has been fully decommissioned.
  const version = mspVersion || serviceDef.versions.at(-1)!.version;
  const allowedMonitoringGroups = monitoringGroupsArchMapper.find(
    (x) => x.monitoring_service_type === monitoringServiceType && x.version === version
  );
  if (allowedMonitoringGroups) {
    monitoringServers = monitoringServers.filter((ms) => {
      if (!allowedMonitoringGroups.monitoring_group_ids.includes(ms.group_id)) {
        return false;
      }
      if (ms.is_nagios && !allowedMonitoringGroups.has_nagios) {
        return false;
      }
      if (!ms.is_nagios && !allowedMonitoringGroups.has_belgrade) {
        return false;
      }
      return true;
    });
  }
  return monitoringServers;
};
